import {
  IFarmInputProduct,
  IFarmInputProductSubCategory,
  IFarmInputProductSubSubCategory,
} from 'constants/interfaces';
import React from 'react';
import {Box, Button, Chip, Grid, Typography} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import {_NumberWithSpaces} from 'constants/index';

type ProductReadOnlyDetailsProps = {
  product?: IFarmInputProduct;
  subCategories: IFarmInputProductSubCategory[];
  subSubCategories: IFarmInputProductSubSubCategory[];
  onClose?: () => any;
};

export function ProductReadOnlyDetails(props: ProductReadOnlyDetailsProps) {
  const {product} = props;

  if (!product) {
    return (
      <div className="generic-form-container product-form product-readonly">
        <div className="form-container">
          <div className="form product-readonly__empty">
            <Typography variant="subtitle1">No product selected.</Typography>
          </div>
        </div>
      </div>
    );
  }

  const category = props.subSubCategories.find(
    (item) => item.id?.toString() === product.subCategoryId?.toString(),
  );
  const parentCategory = props.subCategories.find(
    (item) => item.id?.toString() === category?.subCategoryId?.toString(),
  );
  const categoryLabel = [parentCategory?.description, category?.name]
    .filter(Boolean)
    .join(' / ');

  return (
    <div className="generic-form-container product-form product-readonly">
      <div className="form-container">
        <div className="form">
          <Box className="product-readonly__hero">
            <div className="product-readonly__image-frame">
              {product.image ? (
                <img src={product.image} alt={product.name || 'Product'} />
              ) : (
                <BrokenImageIcon className="product-readonly__image-icon" />
              )}
            </div>
            <div className="product-readonly__hero-content">
              <div className="product-readonly__status-row">
                <span
                  className={`product-readonly__status ${
                    product.isActive ? 'is-active' : 'is-inactive'
                  }`}
                >
                  {product.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="product-readonly__muted">
                  Product #{product.id}
                </span>
              </div>

              <div className="product-readonly__summary-grid">
                <ReadOnlyField label="Stock code" value={product.stockCode} />
                <ReadOnlyField
                  label="Category"
                  value={categoryLabel || product.subCategoryName}
                />
                <ReadOnlyField
                  label="Selling price"
                  value={formatCurrency(product.price)}
                />
                <ReadOnlyField
                  label="Size"
                  value={formatPlainNumber(product.size)}
                />
                <ReadOnlyField label="Unit" value={product.measurement} />
                <ReadOnlyField label="Trade name" value={product.tradeName} />
              </div>
            </div>
          </Box>

          <ReadOnlySection title="Description" columns={1}>
            <ReadOnlyField
              label="Description"
              value={product.description}
              multiline
            />
          </ReadOnlySection>

          <ReadOnlySection title="Product details">
            <ReadOnlyField
              label="Active ingredient"
              value={product.activeIngredient}
            />
            <ReadOnlyLabelField labelLink={product.labelLink} />
            <ReadOnlyChipField
              label="Provincial restrictions"
              values={product.provincialRestrictions}
              getLabel={(province) => province.name}
            />
            <ReadOnlyChipField
              label="Related products"
              values={product.relatedProducts}
              getLabel={(relatedProduct) => relatedProduct.name}
            />
            <ReadOnlyChipField
              label="Bugs"
              values={product.bugs}
              getLabel={(bug) => bug.name}
            />
          </ReadOnlySection>

          <ReadOnlySection title="Dimensions">
            <ReadOnlyField
              label="Length"
              value={formatMeasurement(product.lengthCm, 'cm')}
            />
            <ReadOnlyField
              label="Width"
              value={formatMeasurement(product.widthCm, 'cm')}
            />
            <ReadOnlyField
              label="Height"
              value={formatMeasurement(product.heightCm, 'cm')}
            />
            <ReadOnlyField
              label="Weight"
              value={formatMeasurement(product.weightKg, 'kg')}
            />
          </ReadOnlySection>

          {props.onClose && (
            <div className="product-readonly__actions">
              <Button
                className="secondary-btn"
                variant="contained"
                disableElevation
                onClick={props.onClose}
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReadOnlySection(props: {
  title: string;
  children: React.ReactNode;
  columns?: 1 | 2;
}) {
  return (
    <section className="product-readonly__section">
      <Typography
        variant="subtitle2"
        className="product-readonly__section-title"
      >
        {props.title}
      </Typography>
      <Grid container spacing={2}>
        {React.Children.map(props.children, (child) => (
          <Grid item xs={12} sm={props.columns === 1 ? 12 : 6}>
            {child}
          </Grid>
        ))}
      </Grid>
    </section>
  );
}

function ReadOnlyField(props: {
  label: string;
  value?: string | number | null;
  multiline?: boolean;
}) {
  return (
    <div
      className={`product-readonly__field ${
        props.multiline ? 'is-multiline' : ''
      }`}
    >
      <div className="product-readonly__label">{props.label}</div>
      <div className="product-readonly__value">{formatText(props.value)}</div>
    </div>
  );
}

function ReadOnlyLabelField(props: {labelLink?: string}) {
  return (
    <div className="product-readonly__field">
      <div className="product-readonly__label">Label document</div>
      {props.labelLink ? (
        <Button
          className="product-readonly__link"
          href={props.labelLink}
          target="_blank"
          rel="noreferrer"
          size="small"
          endIcon={<OpenInNewIcon fontSize="small" />}
        >
          Open label
        </Button>
      ) : (
        <div className="product-readonly__value">-</div>
      )}
    </div>
  );
}

function ReadOnlyChipField<T>(props: {
  label: string;
  values?: T[];
  getLabel: (value: T) => string;
}) {
  return (
    <div className="product-readonly__field is-multiline">
      <div className="product-readonly__label">{props.label}</div>
      {!!props.values?.length ? (
        <div className="product-readonly__chips">
          {props.values.map((value, index) => (
            <Chip
              key={`${props.label}-${index}`}
              label={props.getLabel(value)}
              size="small"
              variant="outlined"
            />
          ))}
        </div>
      ) : (
        <div className="product-readonly__value">-</div>
      )}
    </div>
  );
}

function formatText(value?: string | number | null) {
  if (value === undefined || value === null || value === '') return '-';
  return value;
}

function formatPlainNumber(value?: number | string | null) {
  if (value === undefined || value === null || value === '') return undefined;
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function formatCurrencyNumber(value?: number | string | null) {
  if (value === undefined || value === null || value === '') return undefined;
  return _NumberWithSpaces(value as number);
}

function formatCurrency(value?: number | string | null) {
  const formatted = formatCurrencyNumber(value);
  return formatted ? `R ${formatted}` : undefined;
}

function formatMeasurement(value?: number | string | null, unit?: string) {
  const formatted = formatPlainNumber(value);
  return formatted ? `${formatted} ${unit}` : undefined;
}
